// Where everything on the table goes, for a window of a given size. Pure:
// the same table and window always give the same places. Every length is a
// share of the card width (src/config/layout.ts).

import { LAYOUT } from "../config/layout.ts"
import { cardCount, seen, type Card, type CardId, type TableState } from "../domain/engine.ts"
import type { Rings } from "../domain/rings.ts"

export type Geo = {
  readonly W: number
  readonly H: number
  readonly cw: number // card width
  readonly ch: number // card height
  readonly gap: number
  readonly x0: number
  readonly topY: number // the altars' row
  readonly colY: number // the columns' bases
  readonly offsets: readonly number[] // each column's fan: how far apart its cards sit
  readonly compact: boolean
}

export type Point = { readonly x: number; readonly y: number }
export type Where = { readonly kind: "col"; readonly i: number; readonly j: number } | { readonly kind: "altar"; readonly i: number }
export type Place = Point & { readonly z: number; readonly rings: Rings; readonly card: Card; readonly where: Where }

export const colX = (geo: Geo, i: number): number => geo.x0 + i * (geo.cw + geo.gap)
export const altarX = colX

const fanOf = (geo: { readonly cw: number }, step: number, room: number) => (n: number): number =>
  n > 1 ? Math.max(geo.cw * LAYOUT.fan.min, Math.min(geo.cw * step, room / (n - 1))) : 0

// `start` is the rite's first table. It alone sizes the cards and decides
// where the table sits, so neither changes while the rite is played: a fold
// that left nine cards used to drop the whole table lower (Scott,
// 2026-10-20: "it looks glitchy"). `state` spreads each column's fan.
export const layoutOf = (start: TableState, state: TableState, W: number, H: number): Geo => {
  const L = LAYOUT
  const nCols = start.cols.length
  const n = Math.max(nCols, L.minColumns)
  const pad = Math.max(L.pad.min, Math.min(L.pad.max, W * L.pad.ofWidth))
  const gapR = W < L.narrowBelow ? L.gap.narrow : L.gap.wide
  const longest = Math.max(L.minLongest, ...start.cols.map((c) => c.cards.length))
  const byWidth = (W - 2 * pad) / (n + (n - 1) * gapR)
  const byHeight = (H - 2 * pad) / (L.cardRatio + L.altarRow + L.cardRatio + (longest - 1) * L.fanForSizing)
  const cw = Math.max(L.cardWidth.min, Math.min(byWidth, byHeight, L.cardWidth.max))
  const ch = cw * L.cardRatio
  const gap = cw * gapR
  const totalW = nCols * cw + (nCols - 1) * gap
  const x0 = Math.max(pad, (W - totalW) / 2)
  const hasAltars = start.altars.length > 0
  // a small table sits nearer the middle of the window
  const tall = (hasAltars ? ch + cw * L.altarRow : cw * L.altarRow) + ch + (longest - 1) * cw * L.fanForSizing
  const lift = cardCount(start) <= L.smallDeck ? Math.max(0, (H - tall) / L.smallDeckSplit) : 0
  const topY = pad + lift
  const colY = hasAltars ? topY + ch + cw * L.altarRow : topY + cw * L.altarRow
  const room = H - colY - pad - ch
  // On a narrow screen the index marks are drawn larger, so each card in a
  // stack shows more of itself.
  const compact = cw < L.compactBelow
  const fan = fanOf({ cw }, compact ? L.fan.compact : L.fan.wide, room)
  return { W, H, cw, ch, gap, x0, topY, colY, offsets: state.cols.map((c) => fan(c.cards.length)), compact }
}

// Where every card should be, by id, with what it should show.
export const placesOf = (state: TableState, geo: Geo): ReadonlyMap<CardId, Place> => {
  const out = new Map<CardId, Place>()
  for (const [i, col] of state.cols.entries()) {
    const offset = geo.offsets[i] ?? 0
    for (const [j, card] of col.cards.entries()) {
      out.set(card.id, { x: colX(geo, i), y: geo.colY + j * offset, z: 10 + j, rings: seen(card, col.mirror), card, where: { kind: "col", i, j } })
    }
  }
  for (const [i, a] of state.altars.entries()) {
    if (a !== "empty") out.set(a.id, { x: altarX(geo, i), y: geo.topY, z: 10, rings: a.rings, card: a, where: { kind: "altar", i } })
  }
  return out
}
